import { fullName } from "@/common/utils";
import Vue from "vue";

const MAX_CACHE_SIZE = 100;
const PRUNED_CACHE_SIZE = 50;

// Helper functions
const normalizePatient = (patient) => {
	if (!patient?.id) return patient;

	const patientFullName = fullName(patient);

	return {
		id: patient.id,
		fullname: patientFullName?.length > 0 ? patientFullName : undefined,
		email: patient.email?.length > 0 ? patient.email : undefined,
		business: patient.business?.length > 0 ? patient.business : undefined,
		familyId: patient.familyId,
		familyMembers: patient.familyMembers?.length > 0 ? patient.familyMembers : undefined
	};
};

const conversationListModule = {
	namespaced: true,

	state: () => ({
		data: {},
		fixed: {},
		accessMap: {},
		patientIdMap: {}
	}),

	getters: {
		hasPatient: (state) => (csid) => {
			return !!state.data[csid] || !!state.fixed[csid];
		},

		getPatientByConvoSid: (state) => (csid) => {
			if (!csid) return {};
			return state.data[csid] ?? state.fixed[csid] ?? {};
		},

		getPatientById: (state) => (patientId) => {
			if (!patientId) return {};
			const csid = state.patientIdMap[patientId];

			return csid ? (state.data[csid] ?? {}) : {};
		}
	},

	mutations: {
		updatePatient(state, patient) {
			if (!patient?.id) return;

			const csid = state.patientIdMap[patient.id];

			if (csid && state.data[csid]) {
				Vue.set(state.data, csid, normalizePatient(patient));
				state.accessMap[csid] =  Date.now();
			}
		},

		updatePatientByConvo(state, { csid, patient }) {
			if (!patient?.id || !csid) return;

			Vue.set(state.data, csid, normalizePatient(patient));
			state.accessMap[csid] =  Date.now();
			state.patientIdMap[patient.id]= csid;
		},

		deletePatient(state, patientId) {
			if (!patientId) return;

			const csid = state.patientIdMap[patientId];

			if (csid) {
				if (state.fixed[csid]) {
					Vue.delete(state.data, csid);
					delete state.accessMap[csid];
				} else {
					Vue.set(state.data, csid, {});
					state.accessMap[csid] =  Date.now();
				}
				delete state.patientIdMap[patientId];
			}
		},

		removeConversation(state, conversationSid) {
			if (!conversationSid) return;

			if (state.data[conversationSid]) {
				// Remove from patientIdMap before deleting the patient data
				const patientId = state.data[conversationSid]?.id;

				if (patientId) {
					delete state.patientIdMap[patientId];
				}

				Vue.delete(state.data, conversationSid);
				delete state.accessMap[conversationSid];
			}
		},

		updatePatients(state, patients) {
			if (!patients) return;

			const now = Date.now();

			// Add new data and update access timestamps
			Object.entries(patients).forEach(([csid, patient]) => {
				if (patient?.id) {
					Vue.set(state.data, csid, normalizePatient(patient));
					state.accessMap[csid] = now;
					state.patientIdMap[patient.id] = csid;
				}
			});

			// Keep more recent access timestamps
			if (Object.keys(state.data).length > MAX_CACHE_SIZE) {
				const sortedKeys = Object.keys(state.accessMap).sort((a, b) =>
					state.accessMap[b] - state.accessMap[a]
				);

				const prunedData = {};
				const prunedAccessMap = {};
				const prunedPatientIdMap = {};

				sortedKeys.slice(0, PRUNED_CACHE_SIZE).forEach(csid => {
					if (state.data[csid]) {
						prunedData[csid] = state.data[csid];
						prunedAccessMap[csid] = state.accessMap[csid];

						// Update the pruned patient ID map
						const patientId = state.data[csid]?.id;

						if (patientId) {
							prunedPatientIdMap[patientId] = csid;
						}
					}
				});

				state.data = prunedData;
				state.accessMap = prunedAccessMap;
				state.patientIdMap = prunedPatientIdMap;
			}
		},

		addFixedNumber(state, { csid, fixed }) {
			// not a patient
			if (!fixed?.id && !state.fixed[csid]) {
				Vue.set(state.fixed, csid, fixed);
			}
		}
	}
};

export default conversationListModule;